import React, { useEffect } from "react";
import { RouterInputs, api } from "~/utils/api";
import { useForm } from "react-hook-form";
import { Button } from "../v3/themed/Button";
import { toast } from "react-toastify";
import { useSession } from "next-auth/react";
import { If } from "../v3/Conditional";
import Spinner from "../themed/spinner";
import { Label, SubLabel } from "../v3/shadcn/Label";
import { Input } from "../v3/themed/Input";
import { TextArea } from "../v3/themed/TextArea";
import Link from "next/link";
import { EventBySlugOutput } from "~/types/EventTypesInferred";
import Modal from "../v3/themed/Modal";
import { hasEndedUX } from "~/utils/event";

type FormData = RouterInputs["reimbursement"]["createReimbursementRequest"];

type Props = {
    isVisible: boolean;
    setVisible: (show: boolean) => void;
    onCompletion: (
        success: boolean,
        questionnaireCompletionId?: string,
    ) => void;
    event?: EventBySlugOutput;
    communityId?: string;
    reimbursementRequestId?: string;
    mode?: "view";
    kycLink?: string | null;
};

const ReimbursementRequestModal = ({
    isVisible,
    setVisible,
    onCompletion,
    communityId,
    event,
    reimbursementRequestId,
    mode,
    kycLink,
}: Props) => {
    if (!communityId || !event?.id) return null;

    const { data: session } = useSession();
    const form = useForm<FormData>({
        mode: "onChange",
    });

    const { data: user, isLoading: isUserLoading } =
        api.user.getUserById.useQuery(
            {
                userId: session?.user.id ?? "",
            },
            {
                enabled: !!session?.user.id,
            },
        );
    useEffect(() => {
        if (user) {
            form.reset({
                user: {
                    name: user.name ?? "",
                    fullLegalName: user.fullLegalName ?? "",
                    email: user.email ?? "",
                    chatLinks: user.chatLinks,
                    usdcWalletAddress: user.usdcWalletAddress ?? "",
                },
            });
            setTimeout(() => {
                form.trigger();
            }, 0);
        }
    }, [user, form]);

    const { data: reimbursement, isLoading: isReimbursementLoading } =
        api.reimbursement.getById.useQuery(
            {
                communityId: communityId,
                reimbursementRequestId: reimbursementRequestId ?? "",
            },
            {
                enabled: !!reimbursementRequestId && !!communityId,
                onSuccess: (data) => {
                    if (mode === "view" && data) {
                        form.reset({
                            details: data.details ?? "",
                            estimatedCostInUSD: data.amountInUSD,
                            user: {
                                name: data.user.name ?? "N/A",
                                fullLegalName: data.user.fullLegalName ?? "N/A",
                                email: data.user.email ?? "N/A",
                                chatLinks: data.user.chatLinks,
                                usdcWalletAddress:
                                    data.user.usdcWalletAddress ?? "N/A",
                            },
                        });
                    }
                },
            },
        );

    const createReimbursementRequest =
        api.reimbursement.createReimbursementRequest.useMutation();
    const onSubmit = async (data: FormData) => {
        toast.loading("Submitting reimbursement request", {
            toastId: "reimbursement-request",
        });

        await createReimbursementRequest.mutateAsync(
            {
                communityId: communityId,
                eventId: event.id,
                details: data.details,
                estimatedCostInUSD: Number(data.estimatedCostInUSD),
                user: {
                    name: data.user.name,
                    fullLegalName: data.user.fullLegalName,
                    email: data.user.email,
                    chatLinks: !!data.user.chatLinks?.telegram
                        ? {
                              telegram: data.user.chatLinks?.telegram,
                          }
                        : undefined,
                    usdcWalletAddress: data.user.usdcWalletAddress,
                },
            },
            {
                onSuccess: () => {
                    toast.success("Reimbursement request submitted!", {
                        autoClose: 1000,
                    });
                    onCompletion(true);
                    setVisible(false);
                },
                onError: () => {
                    toast.error("Error submitting reimbursement request");
                    onCompletion(false);
                },
                onSettled: () => {
                    toast.dismiss("reimbursement-request");
                },
            },
        );
    };

    if (!user || isUserLoading) {
        return (
            <Modal open={isVisible} onOpenChange={() => setVisible(false)}>
                <Spinner />
            </Modal>
        );
    }

    return (
        <Modal
            open={isVisible}
            onOpenChange={() => setVisible(false)}
            title="Request Event Funding"
            description={
                hasEndedUX(event)
                    ? `Want reimbursement for ${event?.name}`
                    : `Want to host an in-person watch party for ${event?.name}`
            }
        >
            <div className="flex flex-col space-y-4">
                <If condition={!!kycLink}>
                    <Label>KYC - Know Your Customer</Label>
                    <SubLabel>
                        You need to KYC with us to host in-person events.
                        Complete KYC at{" "}
                        <Link
                            className="text-v3-ocean-blue"
                            href={kycLink ?? ""}
                            target="_blank"
                        >
                            {kycLink}
                        </Link>
                        , and if approved there will be no further steps.
                    </SubLabel>
                </If>

                <Label>Details</Label>
                <SubLabel>
                    Tell us more about it. What is the venue, who are you
                    planning to invite, any other goals with the gathering?
                </SubLabel>
                <TextArea
                    placeholder={
                        "I'm planning to host a watch party at my local co-working space. I'm inviting 20 people from the local blockchain community."
                    }
                    className="h-[100px]"
                    disabled={mode === "view"}
                    {...form.register("details", {
                        required: "Details are required",
                    })}
                />
                {form.formState.errors.details && (
                    <p className="text-sm text-red-500">
                        {form.formState.errors.details.message}
                    </p>
                )}

                <Label>USDC Wallet Address</Label>
                <SubLabel>
                    To which you would like to receive the grant
                </SubLabel>
                <Input
                    placeholder={"USDC Wallet Address"}
                    disabled={mode === "view"}
                    {...form.register("user.usdcWalletAddress", {
                        required: "USDC Wallet Address is required",
                        pattern: {
                            value: /^[a-zA-Z0-9]{32,44}$/,
                            message: "Please enter a valid USDC wallet address",
                        },
                    })}
                />
                {form.formState.errors.user?.usdcWalletAddress && (
                    <p className="text-sm text-red-500">
                        {form.formState.errors.user.usdcWalletAddress.message}
                    </p>
                )}

                <Label>Display Name</Label>
                <Input
                    placeholder={"John Doe"}
                    disabled={mode === "view"}
                    {...form.register("user.name", {
                        required: "Display name is required",
                    })}
                />
                {form.formState.errors.user?.name && (
                    <p className="text-sm text-red-500">
                        Display name is required.
                    </p>
                )}

                <Label>Full Name</Label>
                <SubLabel>
                    Legal name you are KYC-ing with. This will not be shown
                    publicly but we will need it to cross-check with your KYC
                    authentication.
                </SubLabel>
                <Input
                    placeholder={"Johnathan Doeburg"}
                    disabled={mode === "view"}
                    {...form.register("user.fullLegalName", {
                        required: "Full name is required",
                    })}
                />
                {form.formState.errors.user?.fullLegalName && (
                    <p className="text-sm text-red-500">
                        Full name is required.
                    </p>
                )}

                <Label>Email</Label>
                <Input
                    placeholder={"johndoe@gmail.com"}
                    disabled={mode === "view"}
                    {...form.register("user.email", {
                        pattern: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        required: "Email is required",
                    })}
                />
                {form.formState.errors.user?.email && (
                    <p className="text-sm text-red-500">
                        Please enter a valid email address.
                    </p>
                )}

                <Label>Telegram Handle</Label>
                <Input
                    placeholder={"@johndoe"}
                    disabled={mode === "view"}
                    {...form.register("user.chatLinks.telegram")}
                />

                <Label>Estimated Cost in USD</Label>
                <SubLabel>Total grant requested for the meet-up.</SubLabel>
                <Input
                    type="number"
                    placeholder={"$100"}
                    disabled={mode === "view"}
                    {...form.register("estimatedCostInUSD", {
                        required: "Estimated cost is required",
                        min: {
                            value: 0,
                            message: "Cost must be a positive number",
                        },
                    })}
                />
                {form.formState.errors.estimatedCostInUSD && (
                    <p className="text-sm text-red-500">
                        Please enter a valid estimated cost.
                    </p>
                )}
            </div>

            <div className="mt-4 flex flex-row items-center">
                <Button
                    className="ml-4 text-background hover:text-tertiary"
                    variant="secondary"
                    onClick={() => {
                        setVisible(false);
                    }}
                >
                    CANCEL
                </Button>
                <Button
                    variant="default"
                    className={`w-[53%] rounded-[26px] border-2 border-background`}
                    size="lg"
                    disabled={
                        !form.formState.isValid ||
                        createReimbursementRequest.isLoading
                    }
                    onClick={form.handleSubmit(onSubmit)}
                >
                    SUBMIT
                </Button>
            </div>
        </Modal>
    );
};

export default ReimbursementRequestModal;
